import React, { useState, useEffect, useRef } from 'react';
import { Badge, Dropdown } from 'react-bootstrap';
import { FaBell, FaBellSlash } from 'react-icons/fa';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationPanel from './NotificationPanel';
import './NotificationBell.css';

const NotificationBell = ({ userId }) => {
    const {
        notifications,
        unreadCount,
        isLoading,
        isConnected,
        initializeNotifications,
        loadNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification
    } = useNotifications();
    
    const [showDropdown, setShowDropdown] = useState(false);
    const bellRef = useRef(null);

    useEffect(() => {
        if (userId && !isConnected) {
            initializeNotifications(userId);
        }
    }, [userId, isConnected]);

    // Add visual feedback for new notifications
    useEffect(() => {
        if (bellRef.current && notifications.length > 0) {
            // Check if this is a new notification (very recent)
            const latestNotification = notifications[0];
            if (latestNotification && new Date(latestNotification.created_at) > new Date(Date.now() - 5000)) {
                bellRef.current.classList.add('notification-bell-shake');
                setTimeout(() => {
                    if (bellRef.current) {
                        bellRef.current.classList.remove('notification-bell-shake');
                    }
                }, 1000);
            }
        }
    }, [notifications]);

    const handleBellClick = () => {
        setShowDropdown(!showDropdown);
    };

    const handleMarkAsRead = async (notificationId) => {
        await markAsRead(notificationId);
    };

    const handleMarkAllAsRead = async () => {
        await markAllAsRead(userId);
    };

    const handleDeleteNotification = async (notificationId) => {
        await deleteNotification(notificationId);
    };

    const handleRefresh = async () => {
        await loadNotifications(userId);
    };

    const getBellIcon = () => {
        if (!isConnected) {
            return <FaBellSlash className="text-muted" />;
        }
        
        return (
            <div className="position-relative">
                <FaBell className={unreadCount > 0 ? 'text-warning' : 'text-secondary'} />
                {unreadCount > 0 && (
                    <Badge 
                        bg="danger" 
                        pill 
                        className="position-absolute notification-badge"
                        style={{ 
                            top: '-8px', 
                            right: '-8px',
                            fontSize: '0.7rem',
                            minWidth: '18px'
                        }}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                )}
            </div>
        );
    };

    return (
        <div className="notification-bell-container">
            <Dropdown 
                show={showDropdown} 
                onToggle={setShowDropdown}
                align="end"
            >
                <Dropdown.Toggle
                    as="button"
                    className="btn btn-link p-2 notification-bell-button"
                    ref={bellRef}
                    onClick={handleBellClick}
                    disabled={isLoading}
                >
                    {getBellIcon()}
                </Dropdown.Toggle>

                <Dropdown.Menu className="notification-dropdown-menu">
                    <NotificationPanel
                        notifications={notifications}
                        unreadCount={unreadCount}
                        isLoading={isLoading}
                        isConnected={isConnected}
                        onMarkAsRead={handleMarkAsRead}
                        onMarkAllAsRead={handleMarkAllAsRead}
                        onDeleteNotification={handleDeleteNotification}
                        onRefresh={handleRefresh}
                    />
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default NotificationBell;